import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * `repairPostThumbnails` callable wrapper hook (admin posts moderation サムネイル修復、新規機能)。
 *
 * 単体 (postIds=[1件]) と一括の両方を扱う。callable の上限 (MAX_BATCH=50) に合わせて 50 件ずつ
 * chunk して逐次呼び出し、集計と進捗を返す。トークン失効回避のため client 既定 70 秒ではなく 9 分。
 */

export interface RepairThumbItemResult {
  postId: string;
  status: 'repaired' | 'skipped' | 'failed';
  photoThumbUrl?: string;
  reason?: string;
}

export interface RepairThumbResult {
  repaired: number;
  skipped: number;
  failed: number;
  results: RepairThumbItemResult[];
}

/** callable 側 MAX_BATCH に合わせる。 */
const CHUNK = 50;

export interface UseRepairPostThumbnailsReturn {
  busy: boolean;
  /** 一括時の進捗 (単体では null)。 */
  progress: { done: number; total: number } | null;
  repair: (districtId: string, postIds: string[]) => Promise<RepairThumbResult>;
}

export function useRepairPostThumbnails(): UseRepairPostThumbnailsReturn {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const repair = useCallback(
    async (districtId: string, postIds: string[]): Promise<RepairThumbResult> => {
      setBusy(true);
      setProgress({ done: 0, total: postIds.length });
      try {
        const fn = httpsCallable<{ districtId: string; postIds: string[] }, RepairThumbResult>(
          functions,
          'repairPostThumbnails',
          { timeout: 540_000 },
        );
        const agg: RepairThumbResult = { repaired: 0, skipped: 0, failed: 0, results: [] };
        for (let i = 0; i < postIds.length; i += CHUNK) {
          const chunk = postIds.slice(i, i + CHUNK);
          const res = await fn({ districtId, postIds: chunk });
          agg.repaired += res.data.repaired;
          agg.skipped += res.data.skipped;
          agg.failed += res.data.failed;
          agg.results.push(...res.data.results);
          setProgress({ done: Math.min(i + chunk.length, postIds.length), total: postIds.length });
        }
        return agg;
      } finally {
        setBusy(false);
        setProgress(null);
      }
    },
    [],
  );

  return { busy, progress, repair };
}
